import React, { useEffect, useState } from 'react';
import ReactHighcharts from 'react-highcharts';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts-more';
import moment from 'moment-timezone';
import { Unit } from '../../helpers/unit';

HighchartsMore(ReactHighcharts.Highcharts);

const GraphView = (props) => {
    let unit = Unit.getInstance();
    let options = {
        titleColor: '#ffffff',
        backgroundColor: '#002651',
        primaryColor: '#0051BD',
        secondaryColor: '#ffffff',
        trendColor: "#FF7F00"
    };

    ReactHighcharts.Highcharts.setOptions({ global: { useUTC: true } });

    const [config, setConfig] = useState(
        {
            credits: {
                enabled: false
            },
            chart: {
                height: 250,
                type: 'area',
                plotBorderWidth: 0,
                plotBorderColor: options.primaryColor,
                backgroundColor: options.backgroundColor,
                marginLeft: 60,
                marginBottom: 43
            },
            title: null,
            plotOptions: {
                area: {
                    marker: {
                        enabled: false,
                        symbol: 'none',
                        radius: 0,
                        states: {
                            hover: {
                                enabled: false
                            }
                        }
                    }
                }
            },
            legend: {
                enabled: false,
            },
            xAxis: {
                gridLineWidth: 0.5,
                gridLineColor: options.primaryColor,
                lineColor: options.primaryColor,
                type: 'datetime',
                tickPositioner: function () {
                    let positions = [],
                        tick = Math.floor(this.dataMin),
                        diff = Math.ceil((this.dataMax - this.dataMin) / (24 * 3600 * 1000)),
                        tickSlot = (diff === 1) ? 6 : 4,
                        increment = Math.ceil((this.dataMax - this.dataMin) / tickSlot);

                    if (this.dataMax !== null && this.dataMin !== null && this.dataMin !== this.dataMax) {
                        for (tick; tick - increment <= this.dataMax; tick += increment) {
                            positions.push(tick);
                        }
                    }
                    return positions;
                },
                labels: {
                    rotation: -33,
                    style: {
                        fontSize: '0.5rem',
                        color: options.titleColor
                    },
                    format: "{value:%I %p}",
                    formatter: function () {
                        const diff = Math.ceil((this.axis.dataMax - this.axis.dataMin) / (24 * 3600 * 1000));
                        return (diff === 1) ? this.axis.defaultLabelFormatter.call(this) : ReactHighcharts.Highcharts.dateFormat('%e %b %I %p', this.value);
                    }
                }
            },
            yAxis: {
                gridLineWidth: 0.5,
                gridLineColor: options.primaryColor,
                title: {
                    text: 'Total Acres',
                    style: { color: options.titleColor, margin: 0 }
                },
                allowDecimals: false,
                labels: {
                    rotation: -60,
                    style: {
                        fontSize: '0.55rem',
                        color: options.titleColor,
                    },
                    formatter: function () {
                        return this.value
                    }
                },
                min: 0
            },
            tooltip: {
                enabled: false,
            },
            series: []
        }
    );

    useEffect(() => {
        bindGraphData(props.graphData);
    }, [props.graphData]);

    useEffect(() => {
        setConfig({
            ...config,
            series: []
        })
    }, [props.tab]);

    const bindGraphData = (graphData) => {
        if (Object.keys(graphData).length > 0 && graphData.isRequesting === false && !props.summaryInfo.isRequesting) {
            let seriesValue = [];
            let plannedLine = [];
            let actualLine = [];
            let trendLine = [];

            for (var i = 0; i < graphData.planned.length; i++) {
                const acres = unit.toArea(graphData.planned[i][1]);
                plannedLine.push([parseUTCTime(graphData.planned[i][0]), acres]);
            };

            for (var j = 0; j < graphData.actual.length; j++) {
                const acres = unit.toArea(graphData.actual[j][1]);
                actualLine.push([parseUTCTime(graphData.actual[j][0]), acres]);
            };

            for (var k = 0; k < graphData.trend.length; k++) {
                const acres = unit.toArea(graphData.trend[k][1]);
                trendLine.push([parseUTCTime(graphData.trend[k][0]), acres]);
            };

            seriesValue.push({
                fillOpacity: 0.5,
                color: options.primaryColor,
                lineWidth: 1.5,
                marker: {
                    enabled: false
                },
                data: plannedLine
            });
            seriesValue.push({
                fillColor: 'none',
                lineWidth: 1.5,
                lineColor: options.titleColor,
                marker: {
                    enabled: false
                },
                data: actualLine
            });
            seriesValue.push({
                fillColor: 'none',
                lineWidth: 1.5,
                lineColor: options.trendColor,
                marker: {
                    enabled: false
                },
                data: trendLine
            });

            const plannedAcres = (graphData.planned.length > 0) ? graphData.planned[graphData.planned.length - 1][1] : 0;
            const actualAcres = (graphData.actual.length > 0) ? graphData.actual[graphData.actual.length - 1][1] : 0;
            const trendAcres = (graphData.trend.length > 0) ? graphData.trend[graphData.trend.length - 1][1] : 0;
            const totalAcres = (plannedAcres + actualAcres + trendAcres);

            let yMax = null;
            let titleText = '';

            if (totalAcres === 0) yMax = 10;
            else yMax = null;

            titleText = `Total ${props.settings.areaShortName}`;
            showLoading(false);

            setConfig({
                ...config,
                series: seriesValue,
                yAxis: {
                    ...config.yAxis,
                    max: yMax,
                    text: titleText,
                },
                title: {
                    ...config.title,
                    text: '',
                }
            });

        } else {
            setConfig({
                ...config,
                title: {
                    text: '<span>Loading...</span>',
                    verticalAlign: 'middle',
                    layout: 'horizontal',
                    itemWidth: 800,
                    useHTML: Boolean,
                    style: { "color": "#fff", "fontSize": "1em", "text-align": "center" }
                },
                series: []
            })

            showLoading(true);
        }
    }

    const parseUTCTime = (dateStr) => {
        return moment.utc(dateStr).valueOf();
    }

    const showLoading = (isShow) => {
        if (isShow) {
            setConfig({
                ...config,
                title: {
                    text: '<span>Loading...</span>',
                    verticalAlign: 'middle',
                    floating: true,
                    useHTML: Boolean,
                    style: { "color": "#fff", "fontSize": "1em", "text-align": "center" }
                },
                series: []
            })
        } else {
            setConfig({
                ...config,
                title: null,
            })
        }
    }

    return (<ReactHighcharts highcharts={Highcharts} config={config} options={config} ></ReactHighcharts>);
}
export default GraphView;